import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import api, { setOnUnauthorized } from "../../../lib/api";
import { apiRetryDelay } from "../../../lib/apiRetry";
import { writeStorage, removeStorage } from "../../../lib/storage-utils";
import { clearUserMessageDrafts } from "../../admin/messages/draftStore";
import { clearOperatorSupport, type SupportSession } from "../../../lib/operatorSupportSession";

interface AuthSetPayload {
  email?: string;
  id?: string;
  admin?: boolean;
  support?: SupportSession | null;
}

interface LoginPayload {
  email: string;
  userId: string;
  isAdmin?: boolean;
  redirectTo?: string;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  userEmail: string;
  userId: string;
  isAdmin: boolean;
  supportSession: SupportSession | null;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [supportSession, setSupportSession] = useState<SupportSession | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clearAuthState = useCallback(() => {
    if (userId) void clearUserMessageDrafts(userId);
    queryClient.clear();
    setIsAuthenticated(false);
    setUserEmail("");
    setUserId("");
    setIsAdmin(false);
    setSupportSession(null);
    clearOperatorSupport();
    removeStorage("email");
    removeStorage("userId");
  }, [queryClient, userId]);

  const setAuthState = useCallback(({ email, id, admin, support }: AuthSetPayload) => {
    setIsAuthenticated(true);
    setUserEmail(email || "");
    setUserId(id || "");
    setIsAdmin(admin || false);
    setSupportSession(support || null);
    if (!support && email) writeStorage("email", email);
    if (!support && id) writeStorage("userId", id);
  }, []);

  const sessionCheckRef = useRef<Promise<boolean> | null>(null);
  const refreshSession = useCallback((): Promise<boolean> => {
    sessionCheckRef.current ??= (async () => {
      for (let attempt = 0; ; attempt += 1) {
        try {
          const { data: session } = await api.get("/auth/session");
          setAuthState({
            email: session.email,
            id: session.userId,
            admin: session.isAdmin,
            support: session.supportSession,
          });
          setIsAuthLoading(false);
          return true;
        } catch (error) {
          // Only the server's verdict ends a session; offline phases, deploy
          // restarts and 5xx responses are retried instead of logging out.
          const status = (error as AxiosError).response?.status;
          if (status === 401 || status === 404) {
            clearAuthState();
            setIsAuthLoading(false);
            return false;
          }
          await new Promise((resolve) => setTimeout(resolve, apiRetryDelay(attempt, error)));
        }
      }
    })().finally(() => {
      sessionCheckRef.current = null;
    });
    return sessionCheckRef.current;
  }, [setAuthState, clearAuthState]);

  const refreshSessionRef = useRef(refreshSession);
  refreshSessionRef.current = refreshSession;

  useEffect(() => {
    void refreshSessionRef.current();
    // A 401 from any endpoint is confirmed via /auth/session before logging out.
    setOnUnauthorized(() => {
      void refreshSessionRef.current();
    });
    return () => setOnUnauthorized(null);
  }, []);

  const login = useCallback(
    async ({ email, userId: id, isAdmin: admin, redirectTo }: LoginPayload) => {
      setAuthState({ email, id, admin });
      navigate(redirectTo || "/dashboard");
    },
    [navigate, setAuthState],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* server unreachable — clear locally anyway */
    }
    clearAuthState();
    navigate("/");
  }, [navigate, clearAuthState]);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isAuthLoading,
      userEmail,
      userId,
      isAdmin,
      supportSession,
      login,
      logout,
      refreshSession,
    }),
    [
      isAuthenticated,
      isAuthLoading,
      userEmail,
      userId,
      isAdmin,
      supportSession,
      login,
      logout,
      refreshSession,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
